/**
 * Routing.
 *
 * TWO AREAS, ONE HARD BOUNDARY
 *
 *   PUBLIC  ( / , /report , /track )  — eagerly loaded, no Firebase, no
 *   analytics, no third-party script except Turnstile. A citizen filing a
 *   report downloads as little as possible and is measured by nobody.
 *
 *   OFFICER ( /sign-in and the app )  — one lazily-loaded chunk containing the
 *   auth context, the shell and every feature screen.
 *
 * The boundary is enforced by the `lazy` imports below: because every officer
 * route resolves through features/officer, nothing in that subtree can end up
 * in the public bundle by accident.
 */

import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { ThemeProvider } from './ThemeProvider';
import { PublicLayout } from '../features/public/PublicLayout';
import { Landing } from '../features/public/Landing';
import { ReportForm } from '../features/public/ReportForm';
import { TrackStatus } from '../features/public/TrackStatus';

const officerChunk = () => import('../features/officer');

/** Route factory: every officer route resolves to the same lazy chunk. */
function officerRoute(path: string, name: keyof Awaited<ReturnType<typeof officerChunk>>) {
  return {
    path,
    lazy: async () => {
      const mod = await officerChunk();
      return { Component: mod[name] as React.ComponentType };
    },
  };
}

/**
 * What is on screen while the officer chunk downloads.
 *
 * React Router warns — "No `HydrateFallback` element provided to render during
 * initial hydration" — when the FIRST route it matches is lazy and nothing says
 * what to show meanwhile. Landing directly on /sign-in or /cases does exactly
 * that, so every officer saw the warning and, more to the point, saw a BLANK
 * PAGE while the chunk arrived.
 *
 * That blank page is the part worth fixing. This bundle is deliberately large —
 * it carries the whole Firebase SDK, which is why it is split out of the public
 * portal at all — and it is downloaded over 2G on a canal bank. Several seconds
 * of nothing is indistinguishable from a broken link.
 *
 * Deliberately plain text and no Firebase import: it has to render before the
 * chunk it is waiting for exists.
 */
function ChunkLoading() {
  return (
    <div
      className="flex min-h-screen items-center justify-center px-4"
      style={{ background: 'var(--bg-app)', color: 'var(--text-muted)' }}
    >
      {/* Not translated, and that is not an oversight: i18n resources live in
          the chunk being fetched. A key name on screen would be worse than a
          word in one language. */}
      <p className="text-sm">Loading…</p>
    </div>
  );
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <PublicLayout />,
    children: [
      { index: true, element: <Landing /> },
      { path: 'report', element: <ReportForm /> },
      { path: 'track', element: <TrackStatus /> },
    ],
  },

  {
    // Pathless layout: provides AuthProvider to everything below it without
    // owning a URL of its own, so the officer paths stay flat and readable.
    lazy: async () => ({ Component: (await officerChunk()).OfficerArea }),
    HydrateFallback: ChunkLoading,
    children: [
      officerRoute('/sign-in', 'SignInRoute'),
      {
        lazy: async () => ({ Component: (await officerChunk()).ShellRoute }),
        children: [
          officerRoute('/dashboard', 'DashboardRoute'),
          officerRoute('/wires', 'WiresRoute'),
          officerRoute('/cases', 'CasesRoute'),
          officerRoute('/recovery', 'RecoveryRoute'),
          officerRoute('/appeals', 'AppealsRoute'),
          officerRoute('/public-reports', 'PublicReportsRoute'),
          officerRoute('/handovers', 'HandoversRoute'),
          officerRoute('/audit', 'AuditRoute'),
          officerRoute('/admin', 'AdminRoute'),
        ],
      },
    ],
  },

  // Unknown path goes to the public landing, not to a dead end.
  { path: '*', element: <Navigate to="/" replace /> },
]);

export function AppRouter() {
  return (
    <ThemeProvider>
      <RouterProvider router={router} />
    </ThemeProvider>
  );
}
